import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { CssBaseline } from '@mui/material';
import { AuthProvider } from './contexts/AuthContext';
import { NotificationProvider } from './contexts/NotificationContext';
import { ProtectedRoute } from './components/ProtectedRoute';
// import DebugInfo from './components/DebugInfo'; // DÉSACTIVÉ
import Login from './pages/Login';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import Dashboard from './pages/Dashboard';
import Courses from './pages/Courses';
import CourseBuilder from './pages/CourseBuilder';
import CourseScormExport from './pages/CourseScormExport';
import ScormExportConnections from './pages/ScormExportConnections';
import CourseStudents from './pages/CourseStudents';
import CourseAnalytics from './pages/CourseAnalytics';
import CourseReminderConfig from './pages/CourseReminderConfig';
import StudentDetail from './pages/StudentDetail';
import Students from './pages/Students';
import Users from './pages/Users';
import Companies from './pages/Companies';
import CompanyStudents from './pages/CompanyStudents';
import StudentImport from './pages/StudentImport';
import StudentMessageComposer from './pages/StudentMessageComposer';
import StudentMessageHistory from './pages/StudentMessageHistory';
import StudentTrackingShell from './components/StudentTrackingShell';
import StudentMessageTemplates from './pages/StudentMessageTemplates';
import StudentMessagesHub from './pages/StudentMessagesHub';
import StudentChatInbox from './pages/StudentChatInbox';
import VerificationCodes from './pages/VerificationCodes';
import StudentMessageBatches from './pages/StudentMessageBatches';
import StudentMessageBatchDetail from './pages/StudentMessageBatchDetail';
import ReminderGlobalDefaults from './pages/ReminderGlobalDefaults';
import StudentTracking from './pages/StudentTracking';
import Profile from './pages/Profile';
import Layout from './components/Layout';
import HomeRedirect from './components/HomeRedirect';
import WysiwygImagePickerHost from './components/WysiwygImagePickerHost';

const theme = createTheme({
  palette: {
    primary: {
      main: '#2eab80', // Vert foncé pour fond blanc
      light: '#4bc99a',
      dark: '#258f6a',
      contrastText: '#fff',
    },
    secondary: {
      main: '#281e30',
      light: '#3d2f4a',
      dark: '#1a1420',
      contrastText: '#fff',
    },
    background: {
      default: '#f5f5f5',
    },
    // Couleur custom pour les fonds violets
    success: {
      main: '#49ffc1', // Vert menthe pour fond violet
      light: '#6fffd4',
      dark: '#3de6ad',
      contrastText: '#281e30',
    },
  },
  typography: {
    fontFamily: '"Lato", "Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h4: {
      fontWeight: 700,
    },
    h5: {
      fontWeight: 700,
    },
    h6: {
      fontWeight: 700,
    },
  },
  shape: {
    borderRadius: 8,
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600,
        },
        containedPrimary: {
          backgroundColor: '#2eab80',
          color: '#fff',
          fontWeight: 700,
          '&:hover': {
            backgroundColor: '#258f6a',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        },
      },
    },
  },
});

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <NotificationProvider>
          <Router>
            <WysiwygImagePickerHost />
            {/* <DebugInfo /> */} {/* DÉSACTIVÉ - Les logs sont dans la console */}
            <Routes>
              {/* Public routes */}
              <Route path="/login" element={<Login />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password/:token" element={<ResetPassword />} />
              
              {/* Protected routes with layout */}
              <Route path="/" element={
                <ProtectedRoute allowedRoles={['admin', 'coach', 'entreprise']}>
                  <Layout />
                </ProtectedRoute>
              }>
                <Route index element={<HomeRedirect />} />
                <Route
                  path="tracking"
                  element={
                    <ProtectedRoute allowedRoles={['coach', 'entreprise']}>
                      <StudentTracking />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="courses"
                  element={
                    <ProtectedRoute allowedRoles={['admin']}>
                      <Courses />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="courses/new"
                  element={
                    <ProtectedRoute allowedRoles={['admin']}>
                      <CourseBuilder />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="courses/:courseId/edit"
                  element={
                    <ProtectedRoute allowedRoles={['admin']}>
                      <CourseBuilder />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="courses/:courseId/scorm"
                  element={
                    <ProtectedRoute allowedRoles={['admin']}>
                      <CourseScormExport />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="courses/:courseId/scorm/:exportId/connections"
                  element={
                    <ProtectedRoute allowedRoles={['admin']}>
                      <ScormExportConnections />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="courses/:courseId/reminders"
                  element={
                    <ProtectedRoute allowedRoles={['admin']}>
                      <CourseReminderConfig />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="courses/:courseId/analytics"
                  element={
                    <ProtectedRoute allowedRoles={['admin', 'coach', 'entreprise']}>
                      <CourseAnalytics />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="courses/:courseId/students"
                  element={
                    <ProtectedRoute allowedRoles={['admin', 'coach', 'entreprise']}>
                      <CourseStudents />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="courses/:courseId/students/:studentId"
                  element={
                    <ProtectedRoute allowedRoles={['admin', 'coach']}>
                      <StudentTrackingShell />
                    </ProtectedRoute>
                  }
                >
                  <Route path="detail" element={<StudentDetail />} />
                  <Route path="messages" element={<StudentMessageHistory />} />
                </Route>
                <Route
                  path="students"
                  element={
                    <ProtectedRoute allowedRoles={['admin']}>
                      <Students />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="users"
                  element={
                    <ProtectedRoute allowedRoles={['admin']}>
                      <Users />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="companies"
                  element={
                    <ProtectedRoute allowedRoles={['admin']}>
                      <Companies />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="companies/:companyId/students"
                  element={
                    <ProtectedRoute allowedRoles={['admin']}>
                      <CompanyStudents />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="students/import"
                  element={
                    <ProtectedRoute allowedRoles={['admin']}>
                      <StudentImport />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="verification-codes"
                  element={
                    <ProtectedRoute allowedRoles={['admin']}>
                      <VerificationCodes />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="chat"
                  element={
                    <ProtectedRoute allowedRoles={['admin']}>
                      <StudentChatInbox />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="chat/:conversationId"
                  element={
                    <ProtectedRoute allowedRoles={['admin']}>
                      <StudentChatInbox />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="student-messages"
                  element={
                    <ProtectedRoute allowedRoles={['admin']}>
                      <StudentMessagesHub />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="student-messages/compose"
                  element={
                    <ProtectedRoute allowedRoles={['admin']}>
                      <StudentMessageComposer />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="student-message-templates"
                  element={<Navigate to="/student-messages/templates" replace />}
                />
                <Route
                  path="student-messages/templates"
                  element={
                    <ProtectedRoute allowedRoles={['admin']}>
                      <StudentMessageTemplates />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="student-messages/reminder-defaults"
                  element={
                    <ProtectedRoute allowedRoles={['admin']}>
                      <ReminderGlobalDefaults />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="student-messages/batches"
                  element={
                    <ProtectedRoute allowedRoles={['admin']}>
                      <StudentMessageBatches />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="student-messages/batches/:batchId"
                  element={
                    <ProtectedRoute allowedRoles={['admin']}>
                      <StudentMessageBatchDetail />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="courses/:courseId/student-messages"
                  element={
                    <ProtectedRoute allowedRoles={['admin']}>
                      <StudentMessageComposer />
                    </ProtectedRoute>
                  }
                />
                <Route path="profile" element={<Profile />} />
              </Route>
              
              {/* Redirect unknown routes */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
            
            {/* Notification Manager - DÉSACTIVÉ TEMPORAIREMENT */}
            {/* <ProtectedRoute allowedRoles={['admin', 'coach']}>
              <NotificationManager />
            </ProtectedRoute> */}
            
            {/* Network Status - DÉSACTIVÉ TEMPORAIREMENT */}
            {/* <NetworkStatus /> */}
          </Router>
        </NotificationProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
